import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { RouteFallback } from '@/components/layout/route-fallback';
import { useAuthStore } from '@/stores/auth';

/**
 * Gates the authenticated application.
 *
 * On a cold load — someone opening a deep link, or returning to an open tab —
 * the shell may have been served without a user (the response was cached, or the
 * session ended since). The store is refreshed once before deciding, so a valid
 * session is not bounced to the login screen just because the inlined payload
 * was stale.
 *
 * This is a routing convenience, not a security control: the API authorises every
 * request independently.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
    const { ready, user, refresh } = useAuthStore();
    const location = useLocation();

    useEffect(() => {
        if (!ready) {
            void refresh();
        }
    }, [ready, refresh]);

    if (!ready) {
        return <RouteFallback />;
    }

    if (!user) {
        // The attempted destination travels with the redirect so that signing in
        // returns the user where they were going, rather than dumping everyone on
        // the dashboard — which matters when the deep link came from an SLA breach
        // notification pointing at a specific complaint.
        return (
            <Navigate
                to="/login"
                replace
                state={{ from: location.pathname + location.search }}
            />
        );
    }

    return <>{children}</>;
}
